import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import { getWidgetTitle, getDefaultDetailBaseURL, generateSlug, getContentCodeByLanguage } from "../../../../../app/src/Utils/utils";
import {
  VIDEO_TYPE_LIVE,
  VIDEO_TYPE_TIIVII_LIVE,
  VIDEO_TYPE_LIVE_TIIVII,
  VIDEO_TYPE_EXTERNAL_LIVE,
} from "../../../../../app/src/Utils/Constants";
import { agendaStatus } from "../../../../../app/src/Utils/agendaDateUtils";
import defaultBanner from "../../../../../app/src/Resources/Images/default_banner.png";
import mediaCoverStyle from "../../../../../app/src/Components/widget_v2/components/MediaCover/style.module.css";
import playlistStyle from "../../../../../app/src/Components/widget_v2/components/PlaylistItem/style.module.css";
import actionButtonsStyle from "../../../../../app/src/Components/widget_v2/components/ActionButtons/style.module.css";

// El banner (miniatura + borde + botón play) reutiliza los mismos módulos CSS que el resto de
// carruseles de la app (MediaCover/PlaylistItem/ActionButtons, widget_v2), igual que hace
// MediaCover.jsx: clases de dos módulos a la vez, una con el estilo base y otra con la regla
// `:hover` (definida sobre `.playlistItemContainer`). Así el borde, el atenuado de la imagen y
// el botón de play solo aparecen en hover -no siempre- y cualquier cambio futuro en esos
// componentes se hereda aquí sin tocar este fichero.
const resolveImage = (content) => content?.banner || content?.poster || content?.background || defaultBanner;

// content.date unifica start/created_on (el BACK decide cuál toca según el contenido venga de un
// live o se haya subido directo a la OTT) y es el mismo valor que se usa para filtrar y ordenar,
// así que no hay que mirar ninguna otra fecha. Llega como ISO con el desfase horario de la
// plataforma ya aplicado ("2026-04-19T00:30:00+02:00"): para el día basta con partir el string,
// SIN pasar por Date/toISOString (eso convertiría a UTC y un contenido de las 00:30 podría
// aparecer un día antes, ya que el filtro de fechas trabaja en días naturales de Europe/Madrid).
const formatCardDate = (date) => {
  const [year, month, day] = date.slice(0, 10).split("-");
  return `${day}/${month}/${year.slice(2)}`;
};

// Etiqueta de estado sobre la miniatura (aspecto de OTT-1953), para cualquier tipo de contenido.
// `start`/`finish` llegan en segundos; null o 0 = sin fecha:
// - sin `start`: sin etiqueta (sin fecha de inicio no se sabe si ya pasó o es futuro);
// - `start` futuro: fecha y hora, en blanco;
// - `start` pasado y `finish` futuro: "EN DIRECTO" en rojo, como la etiqueta de directo de la home;
// - `start` pasado y sin `finish`: "EN DIRECTO" si es un directo (emisión sin fin, p.ej. un canal
//   24h), fecha en gris si no lo es;
// - `start` pasado y `finish` pasado: fecha sin hora, en gris.
// Tipos de directo: los mismos que OTT-1953.
const LIVE_TYPES = [VIDEO_TYPE_LIVE, VIDEO_TYPE_TIIVII_LIVE, VIDEO_TYPE_LIVE_TIIVII, VIDEO_TYPE_EXTERNAL_LIVE];

const isLiveType = (content) => {
  const rawType = content?.type?.name ?? content?.type;
  return typeof rawType === "string" && LIVE_TYPES.includes(rawType.toUpperCase());
};

const isFuture = (seconds) => !!seconds && new Date(seconds * 1000) > new Date();

const formatEventDate = (startTimeMs) =>
  new Date(startTimeMs).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }).toUpperCase();

const formatEventDateTime = (startTimeMs) => {
  const timeLabel = new Date(startTimeMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${formatEventDate(startTimeMs)} ${timeLabel}`;
};

const EventStatusTag = ({ content }) => {
  const { t } = useTranslation();
  const { start, finish } = content || {};

  if (!start) return null;

  if (isFuture(start)) {
    return <div className="bq-status-tag bq-status-tag--scheduled">{formatEventDateTime(start * 1000)}</div>;
  }

  if (isFuture(finish) || (!finish && isLiveType(content))) {
    return <div className="bq-status-tag bq-status-tag--live">{t("subtitle.LIVE").toUpperCase()}</div>;
  }

  return <div className="bq-status-tag bq-status-tag--past">{formatEventDate(start * 1000)}</div>;
};

// Variante de la Agenda (OTT-2011, pantalla 16): solo "EN DIRECTO" (con punto pulsante) o
// "PROGRAMADO", según agendaStatus (finish vacío = en directo hasta start + 2 h). `nowMs` lo
// refresca la página cada 60 s, sin volver a llamar a la API. Un partido que termina con la página
// abierta mantiene "EN DIRECTO" hasta la siguiente carga (la regla de la especificación).
const AgendaStatusTag = ({ status }) => {
  const { t } = useTranslation();
  if (status === "scheduled") {
    return <div className="bq-status-tag ag-status-tag ag-status-tag--scheduled">{t("agenda.scheduled")}</div>;
  }
  return (
    <div className="bq-status-tag ag-status-tag ag-status-tag--live">
      <span className="ag-status-dot" aria-hidden="true" />
      {t("agenda.live")}
    </div>
  );
};

// HH:mm en la zona horaria del navegador (supuestos 17), sin depender del formato del idioma
// (en-US pintaría "8:30 PM").
const formatAgendaTime = (seconds) => {
  const date = new Date(seconds * 1000);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const AgendaCardMeta = ({ content, status }) => {
  const { t } = useTranslation();
  const category = getContentCodeByLanguage(content.category);
  const when = status === "scheduled" ? formatAgendaTime(content.start) : t("agenda.now");
  return <div className="bq-card-date ag-card-meta">{category ? `${category} · ${when}` : when}</div>;
};

// Qué campos pinta la tarjeta, configurable por interfaz (comentario de Diego Dozo en OTT-1966,
// 2026-09-22): viene en `card_fields` de GET /interfaz, lista separada por comas. `null`/`undefined`
// (interfaz todavía sin cargar, o versión de API sin el campo) usa el valor por defecto del
// contrato; "" es un valor real ("solo título") y no debe caer en ese default, de ahí `??` y no `||`.
const DEFAULT_CARD_FIELDS = "category,date";
const parseCardFields = (cardFields) => {
  const raw = cardFields ?? DEFAULT_CARD_FIELDS;
  const fields = new Set(raw.split(",").map((f) => f.trim()).filter(Boolean));
  return { showCategory: fields.has("category"), showDate: fields.has("date"), showSubtitle: fields.has("subtitle") };
};

/**
 * @param {Object} props
 * @param {Object} props.content
 * @param {string} [props.cardFields]
 * @param {"agenda"} [props.variant] OTT-2011: badge EN DIRECTO/PROGRAMADO, meta "CATEGORÍA · HH:mm"
 *   y la misma miniatura que el buscador (banner → poster → background). Sin variante, la tarjeta
 *   del buscador tal cual.
 * @param {number} [props.nowMs] instante de referencia del badge de la variante agenda.
 */
const ResultCard = ({ content, cardFields, variant, nowMs }) => {
  const title = getWidgetTitle(content);
  const slug = title ? generateSlug(title) : null;
  const pathname = `${getDefaultDetailBaseURL()}/${content.id}${slug ? `/${slug}` : ""}`;

  if (variant === "agenda") {
    const status = agendaStatus(content.start, content.finish, nowMs) === "scheduled" ? "scheduled" : "live";
    return (
      <Link to={{ pathname }} className={`bq-card ag-card ${playlistStyle.playlistItemContainer}`}>
        <div className={`${mediaCoverStyle.playlistHeader} ${playlistStyle.playlistHeader}`}>
          <img
            className={`${mediaCoverStyle.playlistItemImage} ${playlistStyle.playlistItemImage}`}
            src={resolveImage(content)}
            alt={title || ""}
            loading="lazy"
          />
          <section className={`${actionButtonsStyle.playFavContainer__top} ${playlistStyle.playFavContainer__top}`}>
            <PlayCircleOutlineIcon id={actionButtonsStyle.playIcon} />
          </section>
          <AgendaStatusTag status={status} />
        </div>
        <AgendaCardMeta content={content} status={status} />
        <div className="bq-card-title">{title}</div>
      </Link>
    );
  }

  const { showCategory, showDate, showSubtitle } = parseCardFields(cardFields);
  const dateLabel = showDate && content.date ? formatCardDate(content.date) : null;
  // Misma función que ya usan MetaData/Slide (widget_v2) para el mismo campo category/subcategory
  // ({code, translations}): prioriza la traducción del idioma activo, si no hay usa `code` tal cual.
  const categoryLabel = showCategory ? getContentCodeByLanguage(content.category) : null;

  return (
    <Link to={{ pathname }} className={`bq-card ${playlistStyle.playlistItemContainer}`}>
      <div className={`${mediaCoverStyle.playlistHeader} ${playlistStyle.playlistHeader}`}>
        <img
          className={`${mediaCoverStyle.playlistItemImage} ${playlistStyle.playlistItemImage}`}
          src={resolveImage(content)}
          alt=""
          loading="lazy"
        />
        <section className={`${actionButtonsStyle.playFavContainer__top} ${playlistStyle.playFavContainer__top}`}>
          <PlayCircleOutlineIcon id={actionButtonsStyle.playIcon} />
        </section>
        <EventStatusTag content={content} />
      </div>
      {(categoryLabel || dateLabel) && (
        <div className="bq-card-date">
          {categoryLabel && <span className="bq-card-category">{categoryLabel}</span>}
          {categoryLabel && dateLabel && <span className="bq-card-date-bar">|</span>}
          {dateLabel}
        </div>
      )}
      <div className="bq-card-title">{title}</div>
      {showSubtitle && content.subtitle && <div className="bq-card-subtitle">{content.subtitle}</div>}
    </Link>
  );
};

export default ResultCard;
